import { faLocationCrosshairs } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AssistChip,
  Checkbox,
  IconButton,
  Skeleton,
  Stack,
  Text,
} from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { ITableRowProps } from '~/components/molecules/Table/TableRow';
import type { IGeolocation } from '~/helpers/types';
import type { IApiCrowdSearchItemResponseBody } from '~/lib/api/modules/Crowd';
import { Duration } from '~/components/atoms/Duration';
import { Empty } from '~/components/atoms/Empty';
import { RelativeTime } from '~/components/atoms/RelativeTime';
import { UserAvatar } from '~/components/atoms/UserAvatar';
import { Table } from '~/components/molecules/Table';
import { parseUserInfo } from '~/helpers/parseUserInfo';
import { useUserBadgesQuery } from '~/lib/api/modules/UserBadges/hooks/useUserBadgesQuery';
import { SearchCommunityListItemAssignedBadge } from './SearchCommunityListItemAssignedBadge';

export type ISearchCommunityListItemProps = ITableRowProps & {
  member: IApiCrowdSearchItemResponseBody;
  selected?: boolean;
  onSelectChange?: (selected: boolean) => void;
  onGeolocate?: (geolocation: IGeolocation) => void;
  onUpdate?: () => void;
};

export const SearchCommunityListItem: React.FC<
  ISearchCommunityListItemProps
> = (props) => {
  const { member, selected, onSelectChange, onGeolocate, onUpdate, ...other } =
    props;
  const userInfo = member.user ? parseUserInfo(member.user) : undefined;
  const { t } = useTranslation();
  const userBadgesQuery = useUserBadgesQuery({
    appId: member.appId,
  });

  const userBadges = userBadgesQuery.data?.userBadges;

  return (
    <Table.Row {...other}>
      <Table.Body>
        <Checkbox
          checked={selected}
          onChange={(event) => onSelectChange?.(event.target.checked)}
        />
      </Table.Body>

      <Stack as={Table.Body} horizontal gap={3}>
        <UserAvatar user={member.user} />
        <Stack gap={1}>
          <Text variant="title" size="sm" truncate>
            {userInfo ? userInfo.fullName : t('common.anonymous')}
          </Text>
          <Text variant="body" size="sm" dimmed>
            {userInfo ? userInfo.emailAddress : `#${member._id}`}
          </Text>
        </Stack>
      </Stack>

      <Table.Body>
        {member.metricsGeoLast && (
          <IconButton
            icon={<FontAwesomeIcon icon={faLocationCrosshairs} />}
            onClick={
              onGeolocate
                ? () =>
                    onGeolocate({
                      longitude: member.metricsGeoLast?.location?.[0] ?? 0,
                      latitude: member.metricsGeoLast?.location?.[1] ?? 0,
                    })
                : undefined
            }
          />
        )}
      </Table.Body>

      <Table.Body>
        {userBadgesQuery.isLoading ? (
          <Skeleton length={{ min: 8, max: 16 }}>
            <AssistChip />
          </Skeleton>
        ) : member.user?.badges?.length ? (
          <Stack gap={1}>
            {member.user?.badges?.map((assignedBadge) => {
              const label =
                userBadges?.find(
                  (userBadge) => userBadge._id === assignedBadge.id,
                )?.name ?? '';

              return (
                <div key={assignedBadge.id}>
                  <SearchCommunityListItemAssignedBadge
                    assignedBadge={assignedBadge}
                    appId={member.appId}
                    userId={member.userId}
                    label={label}
                    onUpdate={onUpdate}
                  />
                </div>
              );
            })}
          </Stack>
        ) : (
          <Empty />
        )}
      </Table.Body>

      <Table.Body>
        {member.totalReadingTime ? (
          <Duration
            ms={member.totalReadingTime}
            human={{ minUnit: 'minutes', unitDisplay: 'short' }}
          />
        ) : (
          <Empty />
        )}
      </Table.Body>

      <Table.Body>
        {member.firstMetricAt && (
          <RelativeTime isoDate={member.firstMetricAt} />
        )}
      </Table.Body>

      <Table.Body>
        {member.lastMetricAt && <RelativeTime isoDate={member.lastMetricAt} />}
      </Table.Body>
    </Table.Row>
  );
};
